import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { PhoneVerificationResponse } from '../api/model/phone-verification-response';
import { VerificationService } from '../service/verification.service';
import { VerificationEventService } from '../service/verification-event.service';

@Injectable()
export class VerificationEventStateHolderService {
  public readonly verificationResponse$: BehaviorSubject<PhoneVerificationResponse | null> =
    new BehaviorSubject<PhoneVerificationResponse | null>(null);
  private readonly eventSource$: BehaviorSubject<ReadableStreamDefaultReader | null> =
    new BehaviorSubject<ReadableStreamDefaultReader | null>(null);

  constructor(
    private readonly verificationService: VerificationService,
    private readonly verificationEventService: VerificationEventService,
  ) {}

  async setVerificationEventSource(uuid: string): Promise<void> {
    const reader = await this.verificationEventService.connect(uuid);
    this.eventSource$.next(reader);
  }

  getVerificationEventSource(): Observable<ReadableStreamDefaultReader | null> {
    return this.eventSource$.asObservable();
  }

  disconnectVerificationStream(uuid: string): void {
    this.verificationEventService.disconnect(uuid);
    this.eventSource$.next(null);
  }

  processReader(reader: ReadableStreamDefaultReader): void {
    this.readFromStream(reader);
  }

  private processResponse(event: string): void {
    const verificationResponse = JSON.parse(event) as PhoneVerificationResponse;
    const { userUuid } = verificationResponse;

    this.verificationResponse$.next(verificationResponse);
    this.eventSource$.next(null);
    this.verificationEventService.disconnect(userUuid);

    this.verificationService
      .processVerifyPhoneNumberResponse(verificationResponse)
      .subscribe();
  }

  private async readFromStream(
    reader: ReadableStreamDefaultReader,
  ): Promise<void> {
    const decoder = new TextDecoder('utf-8');
    const done = false;

    try {
      while (!done) {
        const result = await reader.read();
        if (result.done) {
          break;
        }

        const events = this.decodeStreamValue(decoder, result.value);
        await this.processEvents(events);
      }
    } catch (error) {
      console.error('Error reading SSE stream:', error);
      this.eventSource$.next(null);
    } finally {
      reader.cancel();
    }
  }

  private decodeStreamValue(decoder: TextDecoder, value: Uint8Array): string[] {
    const decoded = decoder.decode(value, { stream: true });
    return decoded.split('\n\n');
  }

  private async processEvents(events: string[]): Promise<void> {
    for (const event of events) {
      const trimmedEvent = event.trim();
      if (!trimmedEvent) continue;

      const eventData = this.extractEventData(trimmedEvent);
      if (eventData) {
        await this.processEventData(eventData);
      }
    }
  }

  private extractEventData(event: string): string | null {
    const lines = event.split('\n');
    // eslint-disable-next-line immutable/no-let
    let data = '';

    for (const line of lines) {
      const match = /^data:(.*)$/.exec(line.trim());
      if (match) {
        data += match[1].trim();
      }
    }

    return data || null;
  }

  private async processEventData(eventData: string): Promise<void> {
    try {
      this.processResponse(eventData);
    } catch (error) {
      console.error('Failed to parse event data:', error);
    }
  }
}
